/**
 * GSC DSP ADAPTER - V2.7 (Duration Truth Patch)
 * Authority: Senior Software Architect
 */

import { AnalyzeFullResult } from "./dspMath";
import { RawDspMetrics, NormalizedDspResult, PeakDetail } from "../types";
import { AUDIT_REPAIR_MODE } from "./auditRepairMode";

function pickDurationSec(raw: AnalyzeFullResult): number {
  const r: any = raw as any;

  // ✅ priorité aux champs "vérité" si présents
  const candidates = [
    r.durationSec,
    r.duration,
    r.sessionDurationSec,
    r.recordingDurationSec,
    r.audioDurationSec,
    r.stuDurationSec,
    r.stuDuration,
  ];

  for (const v of candidates) {
    if (typeof v === "number" && isFinite(v) && v > 0) return v;
  }

  // ⚠️ IMPORTANT: on ne dérive plus la durée depuis l’enveloppe
  // car ça ignore trims, startOffset, hardware lag, etc.
  // Donc fallback conservateur:
  return 1;
}

export function normalizeDspPayload(raw: AnalyzeFullResult): NormalizedDspResult {
  const isMeasurable = raw.rms_db > -90;

  if (isMeasurable && !AUDIT_REPAIR_MODE) {
    const missing = [];
    if (!raw.stuEnvelope || raw.stuEnvelope.length <= 1) missing.push("stuEnvelope");
    if (!raw.chromaHistory || raw.chromaHistory.length === 0) missing.push("chromaHistory");
    if (!raw.stuFingerprint || raw.stuFingerprint.length === 0) missing.push("stuFingerprint");

    if (missing.length > 0) {
      console.warn(
        `[DSP-ADAPTER] Traces manquantes : [${missing.join(", ")}]. Analyse partielle autorisée.`
      );
    }
  }

  let nonSilenceCount = 0;
  const env = raw.stuEnvelope || [];
  const silenceThreshold = 0.005;
  for (let i = 0; i < env.length; i++) if (env[i] > silenceThreshold) nonSilenceCount++;
  const nsr = env.length > 0 ? nonSilenceCount / env.length : 0;

  const envCorr = Math.max(0.1, raw.stability || 0.5);

  const metrics: RawDspMetrics = {
    timingDeviationMs: raw.timingDeviationMs || [],
    onsetTimesMs: raw.onsetTimesMs || [],
    bpmDetected: raw.targetBpm || 60,
    transientCount: raw.onset_count || 0,
    rms_db: raw.rms_db,
    non_silence_ratio: nsr,
    isMeasurable: isMeasurable,
    transient_ratio: raw.transient_ratio || 0,
    peakedness: raw.peakedness || 0,
    stability: raw.stability || 0,
    nuance: { measurable: isMeasurable, relativeDynamics: 1.2, coherence: envCorr },
    onset_count: raw.onset_count || 0,
    envCorr: envCorr,
    lagMs: raw.hardwareLagMs || 0,
    hardwareLagMs: raw.hardwareLagMs || 0,
    startOffsetMs: 0, // Sera rempli par l'orchestrateur
    chromaClarity: raw.chroma_clarity || 0,
    clip_ratio: raw.clip_ratio || 0,
    sessionHealth: Math.min(1.0, (envCorr || 0.8) * 1.2),
  };

  const stuPeaks: PeakDetail[] = (raw.onsetTimesMs || []).map((ms, i) => ({
    time: ms / 1000,
    intensity: raw.onsetIntensities?.[i] || 0.5,
  }));

  const refPeaks: PeakDetail[] = (raw.refOnsetTimesMs || []).map((ms, i) => ({
    time: ms / 1000,
    intensity: raw.refOnsetIntensities?.[i] || 0.5,
  }));

  const durationSec = pickDurationSec(raw);

  return {
    confidence: raw.confidence || 0.1,

    // ✅ DURÉE = vérité (pas envelope)
    duration: durationSec,

    lagMs: raw.hardwareLagMs || 0,
    hardwareLagMs: raw.hardwareLagMs || 0,
    startOffsetMs: 0,

    rawMetrics: metrics,

    envelopes: {
      stu: raw.stuEnvelope || [],
      ref: raw.refEnvelope || [],
      corr: envCorr,
    },

    peaks: { stu: stuPeaks, ref: refPeaks },

    spectral: {
      stuChroma: raw.chromaHistory || [],
      refChroma:
        raw.refChromaHistory && raw.refChromaHistory.length
          ? raw.refChromaHistory
          : raw.chroma
            ? [raw.chroma]
            : [],
      detectedChords: [],
      stuBands: raw.stuBands || { low: 0, mid: 0, high: 0 },
      refBands: raw.refBands || { low: 0, mid: 0, high: 0 },
      stuFingerprint: raw.stuFingerprint || [],
      refFingerprint: raw.refFingerprint || [],
    },

    ghostNotes: raw.ghostNotes || [],
  };
}
